import React, { FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, Grid, MenuItem, Stack, TextField } from '@mui/material'
import { useTranslation } from '@contexts/translation'
import DatePicker from '@components/form/DatePicker'
import { format } from 'date-fns'
import queryObjectFromValues from '@utils/queryObjectFromValues'

interface SupplierOption {
  id: string | number
  name: string
}

interface FiltersFormProps {
  defaultQueries: Record<string, any>
  contracts?: boolean
  contractSupplierOptions?: SupplierOption[]
  operationId?: string | number
  handleClose: () => void
  handleQueries: (queries: Record<string, any>) => void
  resetPage?: () => void
}

interface FormValues {
  supplier?: string
  contract_status?: string
  lessee?: string
  start_date__gte?: Date | null
  start_date__lte?: Date | null
  end_date__gte?: Date | null
  end_date__lte?: Date | null
  notice_date__gte?: Date | null
  notice_date__lte?: Date | null
  [key: string]: any
}

/**
 * FiltersForm Component - Form for filtering table data
 */
const FiltersForm: FC<FiltersFormProps> = ({
  defaultQueries,
  contracts,
  contractSupplierOptions,
  operationId,
  handleClose,
  handleQueries,
  resetPage
}) => {
  const { t, dictionary } = useTranslation()
  const [clearFilter, setClearFilter] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    setValue
  } = useForm<FormValues>({
    defaultValues: {
      supplier: defaultQueries?.supplier || '',
      contract_status: defaultQueries?.contract_status || '',
      lessee: defaultQueries?.lessee || '',
      start_date__gte: defaultQueries?.start_date__gte
        ? new Date(defaultQueries.start_date__gte)
        : null,
      start_date__lte: defaultQueries?.start_date__lte
        ? new Date(defaultQueries.start_date__lte)
        : null,
      end_date__gte: defaultQueries?.end_date__gte
        ? new Date(defaultQueries.end_date__gte)
        : null,
      end_date__lte: defaultQueries?.end_date__lte
        ? new Date(defaultQueries.end_date__lte)
        : null,
      notice_date__gte: defaultQueries?.notice_date__gte
        ? new Date(defaultQueries.notice_date__gte)
        : null,
      notice_date__lte: defaultQueries?.notice_date__lte
        ? new Date(defaultQueries.notice_date__lte)
        : null
    }
  })

  React.useEffect(() => {
    if (clearFilter) {
      handleClose()
      setClearFilter(false)
    }
  }, [clearFilter, handleClose])

  const onSubmit = (values: FormValues) => {
    const queries: Record<string, any> = {
      ...defaultQueries
    }

    if (values.start_date__gte) {
      queries.start_date__gte = format(values.start_date__gte, 'yyyy-MM-dd')
    } else {
      delete queries.start_date__gte
    }

    if (values.start_date__lte) {
      queries.start_date__lte = format(values.start_date__lte, 'yyyy-MM-dd')
    } else {
      delete queries.start_date__lte
    }

    if (values.end_date__gte) {
      queries.end_date__gte = format(values.end_date__gte, 'yyyy-MM-dd')
    } else {
      delete queries.end_date__gte
    }

    if (values.end_date__lte) {
      queries.end_date__lte = format(values.end_date__lte, 'yyyy-MM-dd')
    } else {
      delete queries.end_date__lte
    }

    if (values.notice_date__gte) {
      queries.notice_date__gte = format(values.notice_date__gte, 'yyyy-MM-dd')
    } else {
      delete queries.notice_date__gte
    }

    if (values.notice_date__lte) {
      queries.notice_date__lte = format(values.notice_date__lte, 'yyyy-MM-dd')
    } else {
      delete queries.notice_date__lte
    }

    const obj = queryObjectFromValues(values, ['contract_status', 'supplier'])

    const finalQueries = {
      ...queries,
      ...obj,
      operation: operationId
    }

    if (resetPage) {
      resetPage()
    }
    handleQueries(finalQueries)
    handleClose()
  }

  const onClear = () => {
    reset({
      supplier: '',
      contract_status: '',
      lessee: '',
      start_date__gte: null,
      start_date__lte: null,
      end_date__gte: null,
      end_date__lte: null,
      notice_date__gte: null,
      notice_date__lte: null
    })

    const queries = {
      operation: operationId
    }

    if (resetPage) {
      resetPage()
    }
    handleQueries(queries)
    setClearFilter(true)
  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        {contracts && (
          <>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="supplier"
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="filled"
                    label={dictionary.supplier}
                  >
                    <MenuItem value="">
                      <em>{t('none')}</em>
                    </MenuItem>
                    {contractSupplierOptions?.map(option => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="contract_status"
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="filled"
                    label={dictionary.status}
                  >
                    <MenuItem value="">
                      <em>{t('none')}</em>
                    </MenuItem>
                    <MenuItem value="active">{t('active')}</MenuItem>
                    <MenuItem value="expired">{t('expired')}</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
          </>
        )}

        <Grid item xs={6}>
          <DatePicker
            name="start_date__gte"
            label={t('from') + ' - ' + t('start_date')}
            variant="outlined"
            fullWidth
            control={control}
            clearable
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            name="start_date__lte"
            label={t('to') + ' - ' + t('start_date')}
            variant="outlined"
            fullWidth
            control={control}
            clearable
          />
        </Grid>

        <Grid item xs={6}>
          <DatePicker
            name="end_date__gte"
            label={t('from') + ' - ' + t('end_date')}
            variant="outlined"
            fullWidth
            control={control}
            clearable
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            name="end_date__lte"
            label={t('to') + ' - ' + t('end_date')}
            variant="outlined"
            fullWidth
            control={control}
            clearable
          />
        </Grid>

        <Grid item xs={6}>
          <DatePicker
            name="notice_date__gte"
            label={t('from') + ' - ' + t('notice_date')}
            variant="outlined"
            fullWidth
            control={control}
            clearable
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            name="notice_date__lte"
            label={t('to') + ' - ' + t('notice_date')}
            variant="outlined"
            fullWidth
            control={control}
            clearable
          />
        </Grid>

        <Grid item xs={12}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button color="inherit" onClick={onClear}>
              {t('clear')}
            </Button>
            <Button type="submit" variant="contained" disabled={!isDirty}>
              {t('apply')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}

export default FiltersForm
